import { Trans, useLingui } from "@lingui/react/macro";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useState } from "react";

import { trackEvent } from "@/shared/analytics";
import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

type CopyBuildIdButtonProps = {
  // Номер сборки в игре (`build_id`).
  buildId: number;
  // Только иконка — для узкой карточки на телефоне.
  compact?: boolean;
  // Главное действие (совет в разборе) — залитая кнопка.
  primary?: boolean;
  className?: string;
};

// Копирует номер сборки: в браузере сборок игры поиск по номеру находит ровно одну сборку,
// а названия повторяются (копии, тёзки). Скопировалось — «Номер скопирован». Действие
// `build_copy` уходит в Umami для дашборда команды.
export const CopyBuildIdButton = ({
  buildId,
  compact = false,
  primary = false,
  className,
}: CopyBuildIdButtonProps) => {
  const { t } = useLingui();
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const copied = copiedId === buildId;
  const copy = () => {
    trackEvent("build_copy");
    // В небезопасном контексте (не https) `navigator.clipboard` нет: копировать нечем.
    try {
      navigator.clipboard.writeText(String(buildId)).then(
        () => {
          setCopiedId(buildId);
        },
        () => {
          setCopiedId(null);
        },
      );
    } catch {
      setCopiedId(null);
    }
  };
  const label = copied ? t`Number copied` : t`Copy the number`;
  return (
    <Button
      type="button"
      variant={primary && !copied ? "default" : "outline"}
      size={compact ? "icon" : "default"}
      aria-label={compact ? label : undefined}
      title={compact ? label : undefined}
      onClick={copy}
      className={cn(
        "min-h-11",
        compact ? "size-11" : "px-4",
        copied && "border-good/50 text-good",
        !primary && !copied && "border-border-strong text-accent",
        className,
      )}
    >
      {copied ? <CheckIcon aria-hidden="true" /> : <CopyIcon aria-hidden="true" />}
      {!compact && (copied ? <Trans>Number copied</Trans> : <Trans>Copy the number</Trans>)}
    </Button>
  );
};
